import React, { useState } from 'react';
import { Card, CardContent } from '../components/Card';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Badge } from '../components/Badge';
import { Avatar, AvatarFallback } from '../components/Avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/Sheet';
import { ScrollArea } from '../components/ScrollArea';
import { Search, Plus, Eye, Edit, FileText, Activity, Calendar, User, FileSearch, ArrowRight } from 'lucide-react';
const mockData = [{
  id: '1',
  name: 'William George',
  initials: 'WG',
  admission: 'URGENT',
  date: '12/03/2026',
  sources: 'Lab + Radiology + Notes',
  status: 'Ready'
}, {
  id: '2',
  name: 'Joan Smith',
  initials: 'JS',
  admission: 'EW EMER',
  date: '11/03/2026',
  sources: 'Notes + Vitals',
  status: 'Under Review'
}, {
  id: '3',
  name: 'Mary Williams',
  initials: 'MW',
  admission: 'OBSRV',
  date: '10/03/2026',
  sources: 'Lab + Notes',
  status: 'Draft'
}, {
  id: '4',
  name: 'James Brown',
  initials: 'JB',
  admission: 'URGENT',
  date: '09/03/2026',
  sources: 'Radiology + Notes',
  status: 'Ready'
}, {
  id: '5',
  name: 'Patricia Taylor',
  initials: 'PT',
  admission: 'EW EMER',
  date: '08/03/2026',
  sources: 'Lab + Radiology + Notes',
  status: 'Ready'
}, {
  id: '6',
  name: 'Robert Miller',
  initials: 'RM',
  admission: 'OBSRV',
  date: '07/03/2026',
  sources: 'Notes + Vitals',
  status: 'Under Review'
}, {
  id: '7',
  name: 'Linda Davis',
  initials: 'LD',
  admission: 'URGENT',
  date: '06/03/2026',
  sources: 'Lab + Notes',
  status: 'Ready'
}, {
  id: '8',
  name: 'Michael Wilson',
  initials: 'MW',
  admission: 'EW EMER',
  date: '05/03/2026',
  sources: 'Radiology + Notes',
  status: 'Draft'
}];
const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case 'Ready':
      return 'default';
    // We'll style this green in the className
    case 'Under Review':
      return 'secondary';
    // We'll style this amber
    case 'Draft':
      return 'outline';
    default:
      return 'default';
  }
};
const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'Ready':
      return 'bg-green-100 text-green-800 hover:bg-green-200 border-green-200';
    case 'Under Review':
      return 'bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-200';
    case 'Draft':
      return 'bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-200';
    default:
      return '';
  }
};
export function DischargeSummaries() {
  const [selectedPatient, setSelectedPatient] = useState<(typeof mockData)[0] | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const handleRowClick = (patient: (typeof mockData)[0]) => {
    setSelectedPatient(patient);
    setIsSheetOpen(true);
  };
  return <div className="p-8 max-w-[1600px] mx-auto space-y-8" data-id="element-203">
      <div data-id="element-204">
        <h1 className="font-heading text-4xl font-normal text-foreground mb-2" data-id="element-205">
          Discharge Summaries
        </h1>
        <p className="text-muted-foreground text-lg" data-id="element-206">
          AI-generated summaries from EMR data
        </p>
      </div>

      <div className="flex justify-between items-center gap-4" data-id="element-207">
        <div className="relative flex-1 max-w-md" data-id="element-208">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" data-id="element-209" />
          <Input placeholder="Search by patient name, ID..." className="pl-9 bg-card" data-id="element-210" />
        </div>
        <Button className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2" data-id="element-211">
          <Plus className="w-4 h-4" data-id="element-212" />
          Generate New Summary
        </Button>
      </div>

      <Card data-id="element-213">
        <Table data-id="element-214">
          <TableHeader data-id="element-215">
            <TableRow data-id="element-216">
              <TableHead data-id="element-217">Patient Name</TableHead>
              <TableHead data-id="element-218">Admission Type</TableHead>
              <TableHead data-id="element-219">Discharge Date</TableHead>
              <TableHead data-id="element-220">EMR Sources Pulled</TableHead>
              <TableHead data-id="element-221">Summary Status</TableHead>
              <TableHead className="text-right" data-id="element-222">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-id="element-223">
            {mockData.map(row => <TableRow key={row.id} className="cursor-pointer hover:bg-secondary/50" onClick={() => handleRowClick(row)} data-id="element-224">
                <TableCell className="font-medium" data-id="element-225">
                  <div className="flex items-center gap-3" data-id="element-226">
                    <Avatar className="h-8 w-8 border border-border" data-id="element-227">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs" data-id="element-228">
                        {row.initials}
                      </AvatarFallback>
                    </Avatar>
                    {row.name}
                  </div>
                </TableCell>
                <TableCell data-id="element-229">
                  <Badge variant="outline" className="font-mono text-xs" data-id="element-230">
                    {row.admission}
                  </Badge>
                </TableCell>
                <TableCell data-id="element-231">{row.date}</TableCell>
                <TableCell className="text-muted-foreground" data-id="element-232">
                  {row.sources}
                </TableCell>
                <TableCell data-id="element-233">
                  <Badge variant={getStatusBadgeVariant(row.status)} className={getStatusBadgeClass(row.status)} data-id="element-234">
                    {row.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right" data-id="element-235">
                  <div className="flex justify-end gap-2" data-id="element-236">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-id="element-237">
                      <Eye className="w-4 h-4" data-id="element-238" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" data-id="element-239">
                      <Edit className="w-4 h-4" data-id="element-240" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </Card>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen} data-id="element-241">
        <SheetContent side="right" className="w-[800px] sm:max-w-none p-0 flex flex-col bg-background" data-id="element-242">
          {selectedPatient && <>
              {/* Header */}
              <div className="p-6 border-b border-border bg-card" data-id="element-243">
                <div className="flex justify-between items-start mb-4" data-id="element-244">
                  <div data-id="element-245">
                    <SheetTitle className="font-heading text-2xl" data-id="element-246">
                      {selectedPatient.name}
                    </SheetTitle>
                    <SheetDescription data-id="element-247">
                      MRN: 987654321 • Encounter: ENC-2026-03-12
                    </SheetDescription>
                  </div>
                  <Badge variant={getStatusBadgeVariant(selectedPatient.status)} className={getStatusBadgeClass(selectedPatient.status)} data-id="element-248">
                    {selectedPatient.status}
                  </Badge>
                </div>

                {/* Demographics Summary */}
                <div className="grid grid-cols-4 gap-4 mt-6" data-id="element-249">
                  <div className="flex items-center gap-2 text-sm" data-id="element-250">
                    <User className="w-4 h-4 text-muted-foreground" data-id="element-251" />
                    <span className="text-muted-foreground" data-id="element-252">Age/Gender:</span>
                    <span className="font-medium" data-id="element-253">68 M</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm" data-id="element-254">
                    <Activity className="w-4 h-4 text-muted-foreground" data-id="element-255" />
                    <span className="text-muted-foreground" data-id="element-256">Admission:</span>
                    <span className="font-medium" data-id="element-257">
                      {selectedPatient.admission}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm" data-id="element-258">
                    <Calendar className="w-4 h-4 text-muted-foreground" data-id="element-259" />
                    <span className="text-muted-foreground" data-id="element-260">Admitted:</span>
                    <span className="font-medium" data-id="element-261">08/03/2026</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm" data-id="element-262">
                    <Calendar className="w-4 h-4 text-muted-foreground" data-id="element-263" />
                    <span className="text-muted-foreground" data-id="element-264">Discharged:</span>
                    <span className="font-medium" data-id="element-265">{selectedPatient.date}</span>
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="flex flex-1 overflow-hidden" data-id="element-266">
                {/* Sidebar: EMR Sources */}
                <div className="w-64 border-r border-border bg-card/50 p-4 flex flex-col gap-4" data-id="element-267">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider" data-id="element-268">
                    EMR Sources
                  </h3>
                  <div className="space-y-2" data-id="element-269">
                    <div className="flex items-center gap-2 p-2 rounded-md bg-secondary text-sm font-medium border border-border" data-id="element-270">
                      <FileText className="w-4 h-4 text-primary" data-id="element-271" />
                      Discharge Summary
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary/50 text-sm text-muted-foreground cursor-pointer transition-colors" data-id="element-272">
                      <FileSearch className="w-4 h-4" data-id="element-273" />
                      Radiology Reports (x5)
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary/50 text-sm text-muted-foreground cursor-pointer transition-colors" data-id="element-274">
                      <FileSearch className="w-4 h-4" data-id="element-275" />
                      Urine Culture (x2)
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary/50 text-sm text-muted-foreground cursor-pointer transition-colors" data-id="element-276">
                      <FileSearch className="w-4 h-4" data-id="element-277" />
                      Swab results
                    </div>
                  </div>
                </div>

                {/* Main Content: Summary Text */}
                <ScrollArea className="flex-1 p-6 bg-card" data-id="element-278">
                  <div className="prose prose-sm max-w-none" data-id="element-279">
                    <h3 className="font-heading text-lg font-medium mb-2" data-id="element-280">
                      Presenting Complaint
                    </h3>
                    <p className="text-muted-foreground mb-6" data-id="element-281">
                      Patient presented to the emergency department with a 2-day
                      history of worsening shortness of breath, productive cough
                      with yellow-green sputum, and subjective fevers. He
                      reported right-sided pleuritic chest pain. No hemoptysis,
                      no recent travel, no known sick contacts.
                    </p>

                    <h3 className="font-heading text-lg font-medium mb-2" data-id="element-282">
                      History of Present Illness
                    </h3>
                    <p className="text-muted-foreground mb-6" data-id="element-283">
                      68-year-old male with a past medical history significant
                      for COPD (GOLD stage II), hypertension, and type 2
                      diabetes mellitus. He uses a daily fluticasone/salmeterol
                      inhaler and albuterol as needed. He noticed increased use
                      of his rescue inhaler over the past week prior to
                      admission.
                    </p>

                    <h3 className="font-heading text-lg font-medium mb-2" data-id="element-284">
                      Hospital Course
                    </h3>
                    <p className="text-muted-foreground mb-6" data-id="element-285">
                      Upon admission, patient was hypoxic to 88% on room air,
                      requiring 2L nasal cannula to maintain saturations {'>'}{' '}
                      92%. Chest X-ray revealed a right lower lobe infiltrate
                      consistent with pneumonia. He was started on empiric IV
                      ceftriaxone and azithromycin. Sputum cultures eventually
                      grew Streptococcus pneumoniae, sensitive to ceftriaxone.
                      He was transitioned to oral levofloxacin on hospital day 3
                      as his oxygen requirements decreased. By hospital day 4,
                      he was saturating well on room air and deemed stable for
                      discharge.
                    </p>

                    <h3 className="font-heading text-lg font-medium mb-2" data-id="element-286">
                      Discharge Diagnoses
                    </h3>
                    <ul className="list-disc pl-5 text-muted-foreground mb-6 space-y-1" data-id="element-287">
                      <li data-id="element-288">Community-acquired pneumonia (Right lower lobe)</li>
                      <li data-id="element-289">Acute exacerbation of COPD</li>
                      <li data-id="element-290">Hypertension (stable)</li>
                      <li data-id="element-291">Type 2 Diabetes Mellitus (stable)</li>
                    </ul>

                    <h3 className="font-heading text-lg font-medium mb-2" data-id="element-292">
                      Discharge Medications
                    </h3>
                    <ul className="list-disc pl-5 text-muted-foreground mb-6 space-y-1" data-id="element-293">
                      <li data-id="element-294">Levofloxacin 500mg PO daily for 5 more days</li>
                      <li data-id="element-295">Prednisone 40mg PO daily for 5 days</li>
                      <li data-id="element-296">
                        Continue home medications: Metformin, Lisinopril, Advair
                      </li>
                    </ul>
                  </div>
                </ScrollArea>
              </div>

              {/* Footer CTA */}
              <div className="p-4 border-t border-border bg-card flex justify-end" data-id="element-297">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 px-8" data-id="element-298">
                  Extract Codes
                  <ArrowRight className="w-4 h-4" data-id="element-299" />
                </Button>
              </div>
            </>}
        </SheetContent>
      </Sheet>
    </div>;
}